import { useCallback, useEffect, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { api, errorMessage } from '../api';
import { useCall } from '../call/CallContext';
import { CallDock } from '../call/CallDock';
import { createClientId, formatDate } from '../format';
import type { Conversation, Message, RamiEvent } from '../types';
import { Button, Empty, ErrorNotice, Skeleton, Status } from '../ui';

function eventMessage(event: RamiEvent): Message | null {
  if (!['message.saved', 'message.completed', 'transcript.segment'].includes(event.type)) return null;
  const payload = event.payload;
  if (typeof payload.id !== 'string' || typeof payload.conversationId !== 'string' || typeof payload.text !== 'string') return null;
  const role = payload.role === 'owner' || payload.role === 'system' ? payload.role : 'rami';
  return {
    id: payload.id,
    conversationId: payload.conversationId,
    role,
    text: payload.text,
    createdAt: typeof payload.createdAt === 'string' ? payload.createdAt : event.occurredAt,
    status: 'saved',
    ...(typeof payload.clientMessageId === 'string' ? { clientMessageId: payload.clientMessageId } : {})
  };
}

function mergeMessage(items: Message[], incoming: Message): Message[] {
  const index = items.findIndex((item) => item.id === incoming.id || (incoming.clientMessageId && item.clientMessageId === incoming.clientMessageId));
  if (index < 0) return [...items, incoming];
  return items.map((item, current) => current === index ? { ...item, ...incoming } : item);
}

async function blobToBase64(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let binary = '';
  for (let index = 0; index < bytes.length; index += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(index, index + 0x8000));
  }
  return btoa(binary);
}

type ConversationPreferences = Awaited<ReturnType<typeof api.personaPreferences>>;
type MediaDraft = {
  blob: Blob; kind: 'image' | 'audio'; origin: 'upload' | 'voice_note'; mimeType: string;
  previewUrl: string; durationMs?: number; clientMessageId: string; operationId: string;
};

export function ChatPage() {
  const call = useCall();
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [connected, setConnected] = useState(false);
  const [liveVoiceStatus, setLiveVoiceStatus] = useState<string | null>(null);
  const [autoScroll, setAutoScroll] = useState(true);
  const [olderCursor, setOlderCursor] = useState<string | null>(null);
  const [preferences, setPreferences] = useState<ConversationPreferences | null>(null);
  const [preferenceNotice, setPreferenceNotice] = useState('');
  const [mediaDraft, setMediaDraft] = useState<MediaDraft | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [recording, setRecording] = useState(false);
  const [requestImage, setRequestImage] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const recordingStreamRef = useRef<MediaStream | null>(null);
  const recordingStartedRef = useRef(0);
  const draftRef = useRef(draft);
  draftRef.current = draft;

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [conversations, ownerStatus] = await Promise.all([
        api.conversations(),
        api.ownerStatus().catch(() => null)
      ]);
      setLiveVoiceStatus(ownerStatus?.capabilities.liveVoice ?? null);
      const requestedConversation = new URLSearchParams(window.location.search).get('conversation');
      const current = conversations.items.find((item) => item.id === requestedConversation) ?? conversations.items[0] ?? null;
      setConversation(current);
      if (current) {
        const [page, prefs] = await Promise.all([api.messages(current.id), api.personaPreferences(current.id)]);
        setMessages(page.items);
        setPreferences(prefs);
        setOlderCursor(page.nextCursor ?? null);
      }
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    const source = new EventSource('/api/v1/events', { withCredentials: true });
    source.onopen = () => setConnected(true);
    source.onerror = () => setConnected(false);
    source.onmessage = (raw) => {
      try {
        const event = JSON.parse(raw.data) as RamiEvent;
        sessionStorage.setItem('rami-last-event-id', raw.lastEventId || event.id);
        const message = eventMessage(event);
        if (message) setMessages((items) => mergeMessage(items, message));
      } catch { /* malformed transport events are ignored without rendering unsafe data */ }
    };
    return () => source.close();
  }, []);
  useEffect(() => () => {
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
    recordingStreamRef.current?.getTracks().forEach((track) => track.stop());
    if (mediaDraft) URL.revokeObjectURL(mediaDraft.previewUrl);
  }, [mediaDraft]);
  useEffect(() => {
    if (!autoScroll) return;
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
  }, [autoScroll, messages]);
  useEffect(() => {
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (!draftRef.current.trim()) return;
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, []);

  async function loadOlder() {
    if (!conversation || !olderCursor) return;
    try {
      const page = await api.messages(conversation.id, olderCursor);
      setMessages((items) => [...page.items, ...items]);
      setOlderCursor(page.nextCursor ?? null);
    } catch (caught) { setError(errorMessage(caught)); }
  }

  async function send(event: FormEvent, retry?: Message) {
    event.preventDefault();
    const text = (retry?.text ?? draft).trim();
    if (!text || !conversation) return;
    const clientMessageId = retry?.clientMessageId ?? createClientId();
    const operationId = retry?.operationId ?? createClientId();
    const optimistic: Message = retry ? { ...retry, operationId, status: 'sending' } : {
      id: clientMessageId,
      clientMessageId,
      operationId,
      conversationId: conversation.id,
      role: 'owner',
      text,
      createdAt: new Date().toISOString(),
      status: 'sending'
    };
    setMessages((items) => mergeMessage(items, optimistic));
    if (!retry) setDraft('');
    setSending(true);
    setError('');
    try {
      const result = await api.sendMessage(conversation.id, clientMessageId, operationId, text, requestImage);
      setMessages((items) => mergeMessage(items, { ...result.message, clientMessageId, status: 'saved' }));
      setRequestImage(false);
    } catch (caught) {
      setMessages((items) => items.map((item) => item.clientMessageId === clientMessageId ? { ...item, status: 'failed' } : item));
      setError(errorMessage(caught));
    } finally { setSending(false); }
  }

  async function createConversation() {
    setError(''); setLoading(true);
    try {
      const created = await api.createConversation();
      setConversation(created); setMessages([]); setOlderCursor(null);
      setPreferences(await api.personaPreferences(created.id));
    } catch (caught) { setError(errorMessage(caught)); } finally { setLoading(false); }
  }

  function replaceMediaDraft(next: MediaDraft | null) {
    setMediaDraft((current) => {
      if (current) URL.revokeObjectURL(current.previewUrl);
      return next;
    });
    setUploadProgress(null);
  }

  function chooseImage(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
      setError('الصورة غير مدعومة. استخدم PNG أو JPEG أو WebP.'); return;
    }
    if (file.size > 8 * 1024 * 1024) { setError('حجم الصورة يتجاوز 8 ميجابايت.'); return; }
    replaceMediaDraft({ blob: file, kind: 'image', origin: 'upload', mimeType: file.type,
      previewUrl: URL.createObjectURL(file), clientMessageId: createClientId(), operationId: createClientId() });
  }

  async function startRecording() {
    setError('');
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setError('هذا المتصفح لا يدعم تسجيل الرسائل الصوتية.'); return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const preferred = ['audio/webm;codecs=opus', 'audio/ogg;codecs=opus'].find((type) => MediaRecorder.isTypeSupported(type));
      const recorder = new MediaRecorder(stream, preferred ? { mimeType: preferred } : undefined);
      const chunks: BlobPart[] = [];
      recorder.ondataavailable = (event) => { if (event.data.size > 0) chunks.push(event.data); };
      recorder.onstop = () => {
        stream.getTracks().forEach((track) => track.stop()); recordingStreamRef.current = null;
        const durationMs = Math.max(0, Date.now() - recordingStartedRef.current);
        const blob = new Blob(chunks, { type: recorder.mimeType || 'audio/webm' });
        if (blob.size > 0) replaceMediaDraft({ blob, kind: 'audio', origin: 'voice_note', mimeType: blob.type,
          previewUrl: URL.createObjectURL(blob), durationMs, clientMessageId: createClientId(), operationId: createClientId() });
        setRecording(false);
      };
      recorder.onerror = () => { setError('تعذر إكمال التسجيل الصوتي.'); setRecording(false); };
      recorderRef.current = recorder; recordingStreamRef.current = stream; recordingStartedRef.current = Date.now();
      recorder.start(250); setRecording(true);
    } catch (caught) {
      setError(caught instanceof DOMException && caught.name === 'NotAllowedError'
        ? 'لم يُسمح باستخدام الميكروفون. يمكنك تعديل الإذن من إعدادات المتصفح.' : 'تعذر بدء التسجيل الصوتي.');
    }
  }

  function stopRecording() { if (recorderRef.current?.state === 'recording') recorderRef.current.stop(); }

  async function sendMedia() {
    if (!conversation || !mediaDraft) return;
    setSending(true); setError(''); setUploadProgress(0);
    try {
      await api.sendConversationMedia({
        conversationId: conversation.id, clientMessageId: mediaDraft.clientMessageId,
        operationId: mediaDraft.operationId, kind: mediaDraft.kind, origin: mediaDraft.origin,
        mimeType: mediaDraft.mimeType, dataBase64: await blobToBase64(mediaDraft.blob),
        ...(draft.trim() ? { caption: draft.trim() } : {}),
        ...(mediaDraft.durationMs === undefined ? {} : { durationMs: mediaDraft.durationMs })
      }, setUploadProgress);
      const page = await api.messages(conversation.id);
      setMessages(page.items); setDraft(''); replaceMediaDraft(null);
    } catch (caught) { setError(errorMessage(caught)); setUploadProgress(null); }
    finally { setSending(false); }
  }

  async function savePreferences(patch: Partial<ConversationPreferences>) {
    if (!conversation || !preferences) return;
    setPreferenceNotice('جارٍ تطبيق الإعداد على الرد التالي…');
    try {
      const saved = await api.savePersonaPreferences({ conversationId: conversation.id, expectedRevision: preferences.revision, ...patch });
      setPreferences(saved); setPreferenceNotice('حُفظ. سيبدأ أثره مع الرد التالي، ولن يغيّر ردًا قيد التنفيذ.');
    } catch (caught) { setError(errorMessage(caught)); setPreferenceNotice(''); }
  }

  const inviteId = new URLSearchParams(window.location.search).get('invite');
  return <main className="chat-layout">
    <section className="conversation" aria-labelledby="conversation-title">
      <header className="conversation__header">
        <div><p className="eyebrow">حديث مستمر</p><h1 id="conversation-title">{conversation?.title || 'رامي'}</h1></div>
        <Status tone={connected ? 'good' : 'warning'}>{connected ? 'متصل بالتحديثات' : 'جارٍ استعادة التحديثات'}</Status>
      </header>
      {inviteId ? <div className="invite-card" role="status"><div><strong>لديك دعوة مكالمة</strong><p>لن يبدأ الصوت أو استخدام الميكروفون قبل قبولك.</p></div><div className="inline-actions"><Button onClick={() => void call.start({ inviteId })}>قبول الدعوة</Button><Button variant="secondary" onClick={() => { void api.rejectInvite(inviteId); window.history.replaceState(null, '', '/'); }}>رفض</Button></div></div> : null}
      {error ? <ErrorNotice message={error} onRetry={() => void load()} /> : null}
      <div className="message-list" ref={listRef} onScroll={(event) => {
        const node = event.currentTarget;
        setAutoScroll(node.scrollHeight - node.scrollTop - node.clientHeight < 80);
      }} aria-live="polite" aria-busy={loading}>
        {olderCursor ? <Button variant="ghost" onClick={() => void loadOlder()}>تحميل رسائل أقدم</Button> : null}
        {loading ? <Skeleton lines={5} /> : !conversation ? <Empty title="لا توجد محادثة بعد"><p>ابدأ محادثة جديدة دون استعادة أي محتوى حُذف سابقًا.</p><Button onClick={() => void createConversation()}>بدء محادثة</Button></Empty> : !messages.length ? <Empty title="ابدأ على راحتك">اكتب أول رسالة، وسيحفظ رامي السياق من الخادم.</Empty> : messages.map((message) => <article className={`message message--${message.role}`} key={message.id}>
          <div className="message__meta"><strong>{message.role === 'owner' ? 'أنت' : message.role === 'rami' ? 'رامي' : 'النظام'}</strong><time dateTime={message.createdAt}>{formatDate(message.createdAt)}</time></div>
          <p>{message.text}</p>
          {message.media?.map((asset) => <div className="message__media" key={asset.id}>
            {asset.kind === 'image' ? <img src={api.mediaUrl(asset.id)} alt={asset.transcript || 'صورة مرفقة'} loading="lazy" /> : null}
            {asset.kind === 'audio' ? <audio controls preload="metadata" src={api.mediaUrl(asset.id)} /> : null}
            {asset.kind === 'audio' ? <details><summary>النص المفرغ — {asset.transcriptStatus === 'ready' ? 'جاهز' : asset.transcriptStatus === 'failed' ? 'تعذر' : asset.transcriptStatus === 'blocked' ? 'أُوقف' : 'قيد المعالجة'}</summary><p>{asset.transcript || 'لم يجهز النص بعد.'}</p></details> : null}
            {asset.kind === 'image' && asset.analysisStatus !== 'ready' ? <small>{asset.analysisStatus === 'failed' ? 'تعذر تحليل الصورة' : asset.analysisStatus === 'blocked' ? 'أُوقف تحليل الصورة' : 'تحليل الصورة قيد المعالجة'}</small> : null}
          </div>)}
          {message.status ? <div className="message__status">{message.status === 'sending' ? 'جارٍ الإرسال' : message.status === 'saved' ? 'حُفظت الرسالة' : message.status === 'responding' ? 'جارٍ الرد' : <><span>تعذر الإرسال</span><button onClick={(event) => void send(event, message)}>إعادة المحاولة</button></>}</div> : null}
        </article>)}
      </div>
      {!autoScroll ? <Button variant="secondary" onClick={() => { setAutoScroll(true); listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' }); }}>آخر الحديث</Button> : null}
      {conversation && preferences ? <section className="composer-settings" aria-label="إعدادات رد رامي لهذه المحادثة">
        <label>صيغة الرد<select value={preferences.replyMode} onChange={(event) => void savePreferences({ replyMode: event.target.value as ConversationPreferences['replyMode'] })}>
          <option value="text">نص</option><option value="voice_note">رسالة صوتية مسجلة</option>
        </select></label>
        <label>صور رامي<select value={preferences.outgoingImageMode} onChange={(event) => void savePreferences({ outgoingImageMode: event.target.value as ConversationPreferences['outgoingImageMode'] })}>
          <option value="generate">توليد صورة</option><option value="internet">البحث عن صورة مناسبة</option><option value="off">لا يرسل صورًا</option>
        </select></label>
        <label className="toggle"><input type="checkbox" checked={preferences.ambientEnabled} onChange={(event) => void savePreferences({ ambientEnabled: event.target.checked })} /> صوت محيط</label>
        <label>مستوى المحيط <input aria-label="مستوى الصوت المحيط" type="range" min="0" max="0.4" step="0.05" value={preferences.ambientLevel} disabled={!preferences.ambientEnabled} onChange={(event) => void savePreferences({ ambientLevel: Number(event.target.value) })} /></label>
        {preferenceNotice ? <small role="status">{preferenceNotice}</small> : null}
      </section> : null}
      {mediaDraft ? <section className="media-draft" aria-label="معاينة الوسيط قبل الإرسال">
        {mediaDraft.kind === 'image' ? <img src={mediaDraft.previewUrl} alt="معاينة الصورة قبل الإرسال" /> : <audio controls src={mediaDraft.previewUrl} />}
        <div className="inline-actions"><Button onClick={() => void sendMedia()} busy={sending}>إرسال الوسيط</Button><Button variant="secondary" onClick={() => replaceMediaDraft(null)}>إلغاء</Button></div>
        {uploadProgress !== null ? <label>تقدم الرفع <progress max="100" value={uploadProgress}>{uploadProgress}%</progress> {uploadProgress}%</label> : null}
      </section> : null}
      <form className="composer" onSubmit={send}>
        <label htmlFor="message-input" className="sr-only">اكتب رسالة</label>
        <textarea id="message-input" rows={2} value={draft} onChange={(event) => setDraft(event.target.value)} placeholder="اكتب لرامي…" onKeyDown={(event) => {
          if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); event.currentTarget.form?.requestSubmit(); }
        }} />
        <input ref={fileRef} className="sr-only" type="file" accept="image/png,image/jpeg,image/webp" capture="environment" onChange={chooseImage} />
        <Button type="button" variant="secondary" disabled={!conversation || Boolean(mediaDraft)} onClick={() => fileRef.current?.click()}>صورة</Button>
        {recording ? <Button type="button" variant="danger" onClick={stopRecording}>إيقاف التسجيل</Button> : <Button type="button" variant="secondary" disabled={!conversation || Boolean(mediaDraft)} onClick={() => void startRecording()}>تسجيل صوتي</Button>}
        {preferences?.outgoingImageMode !== 'off' ? <label className="toggle"><input type="checkbox" checked={requestImage} onChange={(event) => setRequestImage(event.target.checked)} /> أطلب صورة مع الرد</label> : null}
        <Button type="submit" busy={sending} disabled={!draft.trim() || !conversation || Boolean(mediaDraft)}>إرسال</Button>
      </form>
    </section>
    <CallDock conversationId={conversation?.id} liveVoiceStatus={liveVoiceStatus} />
  </main>;
}
